import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Modal from "components/shared/modal/Modal";
import LabelledTextInput from "components/shared/LabelledTextInput";
import { useForm } from "react-hook-form";
import { Button } from "components/shared/standardElements";
import Loading from "components/shared/Loading";
import { toast } from "react-toastify";
import styled from "styled-components/macro";
import {
  COLORS,
  FONTSIZE,
  FONTWEIGHT,
  CONSTANTS
} from "helpers/designConstants";
import { useAuth } from "database/useAuth";

//Extends Modal Component
const EditLoggedInUser = ({ show, onClose, title, user, ...props }) => {
  //HOOKS
  const { t } = useTranslation();
  const {
    register: registerDetailsForm,
    handleSubmit: handleChangeDetailsSubmit,
    formState: { errors: userDetailsErrors },
    getValues: getUserDetailsValues,
    reset: restUserDetailsForm,
    watch: watchUserDetails
  } = useForm();
  const {
    register: registerChangePasswordForm,
    handleSubmit: handlePasswordChangeSubmit,
    getValues: getPasswordChangeValues,
    formState: { errors: newPasswordErrors },
    reset: resetPasswordForm,
    watch: watchPasswordFields
  } = useForm();

  const { changePassword, updateUserDetails, reauthenticateUser } = useAuth();

  const [showLoading, setShowLoading] = useState(false);
  const [showEnterCurrentPassword, setShowEnterCurrentPassword] =
    useState(false);
  const [reauthReason, setReauthReason] = useState();
  const [nameFieldValue, setNameFieldValue] = useState();
  const [emailFieldValue, setEmailFieldValue] = useState();
  const [newPasswordFieldValue, setNewPasswordFieldValue] = useState();
  const [verifyPasswordFieldValue, setVerifyPasswordFieldValue] = useState();

  const currentPasswordRef = useRef();

  useEffect(() => {
    const subscriptionToUserDetails = watchUserDetails((value) => {
      setNameFieldValue(value.userName);
      setEmailFieldValue(value.email);
    });
    const subscriptionToPasswordFields = watchPasswordFields((value) => {
      setNewPasswordFieldValue(value.newPassword);
      setVerifyPasswordFieldValue(value.verifyPassword);
    });
    return () => {
      subscriptionToUserDetails.unsubscribe();
      subscriptionToPasswordFields.unsubscribe();
    };
  }, [watchUserDetails, watchPasswordFields]);

  const onUpdateUser = async (data) => {
    setShowLoading(true);
    try {
      await updateUserDetails(data.userName, data.email);
      setShowLoading(false);
      toast.success(t("user_update_notification"));
      onClose();
    } catch (error) {
      setShowLoading(false);

      if (error.code === "auth/requires-recent-login") {
        setReauthReason("userDetails");
        setShowEnterCurrentPassword(true);
      }

      let errorMessage = "";
      if (error.code === "auth/email-already-in-use") {
        errorMessage = t("email_exists_error");
      } else {
        errorMessage = t("generic_error");
      }
      toast.error(errorMessage);
      console.log(error.code);
    }
  };

  const onChangePassword = async (data) => {
    setShowLoading(true);
    try {
      await changePassword(data.newPassword);
      setShowLoading(false);
      toast.success(t("password_updated_notification"));
      onClose();
      restUserDetailsForm();
      resetPasswordForm();
    } catch (error) {
      setShowLoading(false);
      if (error.code === "auth/requires-recent-login") {
        setReauthReason("changePassword");
        setShowEnterCurrentPassword(true);
      }
      console.log(error);
    }
  };

  const reauthenticate = async () => {
    setShowLoading(true);
    await reauthenticateUser(currentPasswordRef.current.value);
    setShowLoading(false);
    setShowEnterCurrentPassword(false);
    if (reauthReason === "userDetails") {
      onUpdateUser(getUserDetailsValues());
    } else {
      onChangePassword(getPasswordChangeValues());
    }
  };

  const closeModal = () => {
    restUserDetailsForm();
    resetPasswordForm();
    onClose();
  };

  return (
    <Modal
      disableCloseOnOutsideClick
      title={title}
      show={show}
      onClose={closeModal}
      {...props}
    >
      <Container>
        <StyledForm onSubmit={handleChangeDetailsSubmit(onUpdateUser)}>
          <InnerFormContainer>
            <InnerFormTitle>{t("user_details_title")}</InnerFormTitle>
            <InputAndErrorWrapper>
              <StyledTextInput
                labelText={t("name_title")}
                type='text'
                id='userName'
                name='userName'
                placeholder={t("name_title")}
                defaultValue={user?.displayName}
                {...registerDetailsForm("userName", {
                  required: t("name_required_error")
                })}
              />
              {userDetailsErrors.userName && (
                <StyledInputErrorMessage>
                  {userDetailsErrors.userName.message}
                </StyledInputErrorMessage>
              )}
            </InputAndErrorWrapper>
            <InputAndErrorWrapper>
              <StyledTextInput
                labelText={t("email_address")}
                type='email'
                id='email'
                name='email'
                placeholder={t("email_address")}
                defaultValue={user?.email}
                {...registerDetailsForm("email", {
                  required: t("email_required_error"),
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: t("invalid_email_error")
                  }
                })}
              />
              {userDetailsErrors.email && (
                <StyledInputErrorMessage>
                  {userDetailsErrors.email.message}
                </StyledInputErrorMessage>
              )}
            </InputAndErrorWrapper>
            <StyledButton
              primary
              disabled={
                (nameFieldValue === user?.displayName &&
                  emailFieldValue === user?.email) ||
                nameFieldValue == null ||
                emailFieldValue == null ||
                nameFieldValue === "" ||
                emailFieldValue === ""
              }
            >
              {t("update_button")}
            </StyledButton>
          </InnerFormContainer>
        </StyledForm>
        <Divider />
        <StyledForm onSubmit={handlePasswordChangeSubmit(onChangePassword)}>
          <InnerFormContainer>
            <InnerFormTitle>{t("change_password_title")}</InnerFormTitle>
            <InputAndErrorWrapper>
              <StyledTextInput
                labelText={t("new_password_label")}
                type='password'
                id='newPassword'
                name='newPassword'
                placeholder={t("new_password_label")}
                {...registerChangePasswordForm("newPassword")}
                {...registerChangePasswordForm("newPassword", {
                  minLength: {
                    value: 6,
                    message: t("password_length_error")
                  }
                })}
              />
              {newPasswordErrors.newPassword && (
                <StyledInputErrorMessage>
                  {newPasswordErrors.newPassword.message}
                </StyledInputErrorMessage>
              )}
            </InputAndErrorWrapper>
            <InputAndErrorWrapper>
              <StyledTextInput
                labelText={t("verify_password_label")}
                type='password'
                id='verifyPassword'
                name='verifyPassword'
                placeholder={t("verify_password_label")}
                {...registerChangePasswordForm("verifyPassword", {
                  validate: (value) =>
                    value === getPasswordChangeValues("newPassword") ||
                    t("error_password_match")
                })}
              />
              {newPasswordErrors.verifyPassword && (
                <StyledInputErrorMessage>
                  {newPasswordErrors.verifyPassword.message}
                </StyledInputErrorMessage>
              )}
            </InputAndErrorWrapper>
            <StyledButton
              primary
              disabled={
                newPasswordFieldValue == null ||
                verifyPasswordFieldValue == null ||
                newPasswordFieldValue?.length === 0 ||
                verifyPasswordFieldValue?.length === 0
              }
            >
              {t("update_button")}
            </StyledButton>
          </InnerFormContainer>
        </StyledForm>
      </Container>
      <Modal
        closeButtonHidden
        disableCloseOnOutsideClick
        title={t("current_password_title")}
        show={showEnterCurrentPassword}
        onClose={closeModal}
      >
        <Container>
          <InnerFormContainer>
            <InputAndErrorWrapper>
              <StyledTextInput
                labelText={t("current_password_label")}
                type='password'
                id='currentPassword'
                name='currentPassword'
                placeholder={t("current_password_label")}
                ref={currentPasswordRef}
              />
            </InputAndErrorWrapper>
            <Button onClick={reauthenticate}>Submit</Button>
          </InnerFormContainer>
        </Container>
      </Modal>
      <Loading isLoading={showLoading} />
    </Modal>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  align-items: stretch;
`;

const InnerFormContainer = styled.div`
  /* margin-bottom: 25px; */
  padding: 10px;
  min-width: 300px;
  /* border: 1px solid ${COLORS.secondary}; */
  /* border-radius: ${CONSTANTS.borderRadius}; */
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const InnerFormTitle = styled.div`
  color: ${COLORS.text};
  font-weight: ${FONTWEIGHT.heavy};
  text-transform: uppercase;
  margin-bottom: 5px;
`;

const Divider = styled.div`
  height: 1px;
  margin: 15px 10px;
  background-color: ${COLORS.text};
`;

const InputAndErrorWrapper = styled.div`
  width: 100%;
  margin-bottom: 8px;
`;

const StyledTextInput = styled(LabelledTextInput)``;

const StyledInputErrorMessage = styled.div`
  padding: 3px 0 0 5px;
  color: ${COLORS.alertRed};
  font-size: ${FONTSIZE.xsmall};
  align-self: flex-start;
`;

const StyledButton = styled(Button)`
  margin-top: 10px;
  align-self: flex-end;
`;

export default EditLoggedInUser;
